import { useCallback, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { useHistory } from 'hooks/useHistory'
import { CREATE_QUESTION_PASSAGE } from 'queries/questionPassage'
import { CreateQuestionPassageInput } from '__generatedTypes__/globalTypes'
import ItemCreatedModal from 'components/Modals/ItemCreatedModal'
import { toast } from 'utils/toast'
import { createCARSQuestion, createCARSQuestionVariables } from '__generatedTypes__/createCARSQuestion'
import { PageLoader } from 'components/PageLoader'
import { Form, FormStateApi } from '../Form'

export const Create = () => {
  const { navigate } = useLink()
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [create] = useMutation<createCARSQuestion, createCARSQuestionVariables>(CREATE_QUESTION_PASSAGE)
  const resetFormRef = useRef<(() => void) | undefined>()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleSubmit = useCallback(
    async (questionPassage: CreateQuestionPassageInput, formApi: FormStateApi) => {
      try {
        const { data } = await create({
          variables: {
            params: {
              ...questionPassage,
              history: populateHistoryWithUser(questionPassage.history),
            },
          },
        })

        if (data?.createCARSQuestion.id) {
          setIsModalOpen(true)
          resetFormRef.current = formApi.restart
        } else {
          toast.error('Failed to create question')
        }
      } catch (error) {
        toast.error('Failed to create question')
      }
    },
    [populateHistoryWithUser, create]
  )

  const handleOpenQuestionList = useCallback(() => {
    setIsModalOpen(false)
    navigate(cmsRoutes.CARS_QUESTION_LIST)
  }, [navigate])

  const handleAddNewQuestion = useCallback(() => {
    setIsModalOpen(false)
    resetFormRef.current?.()
  }, [])

  if (loadingHistory) {
    return <PageLoader />
  }

  return (
    <>
      <Form onSubmit={handleSubmit} />
      <ItemCreatedModal
        open={isModalOpen}
        title="Question Created!"
        subtitle={
          <>
            The question successfully uploaded.
            <br />
            What do you want to do next?
          </>
        }
        firstOptionText="Go back to the Question page"
        secondOptionText="Add a New Question"
        onFirstOptionClick={handleOpenQuestionList}
        onSecondOptionClick={handleAddNewQuestion}
        onClose={handleOpenQuestionList}
      />
    </>
  )
}
